import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from "react";
import { useNavigate } from "react-router-dom";
import useAuth from "../hooks/useAuth";
import LoginSheet from "../componments/dialog/login-sheet/login-sheet";

//登入閘門
//
//  為什麼需要它：如果用「路由守門」處理未登入（進到頁面才擋），
//  使用者會先被帶離目前畫面，彈窗後面是一片空白 —— 看起來像整頁不見了。
//  改成在原地攔截：沒登入就「不換頁」，直接在目前畫面上跳彈窗，
//  背景仍是使用者本來在看的內容，登入完成再導去他原本想去的地方。
//
//  用法：
//    const { requireLogin } = useLoginGate();
//    requireLogin("/account", "登入後即可查看你的報告。");
//
//  路由守門（guard/require-user-auth）仍然保留 —— 直接貼網址進來時還是要擋。

const LoginGateContext = createContext(null);

export const LoginGateProvider = ({ children }) => {
  //gate 為 null 代表沒有待處理的登入；有值時記著「登入完要去哪」
  const [gate, setGate] = useState(null);
  const { isLoggedIn } = useAuth();
  const navigate = useNavigate();

  //已登入 → 直接導頁並回傳 true
  //未登入 → 跳彈窗並回傳 false（呼叫端可據此決定要不要做別的事）
  const requireLogin = useCallback(
    (to, message) => {
      if (isLoggedIn) {
        if (to) navigate(to);
        return true;
      }
      setGate({ to, message });
      return false;
    },
    [isLoggedIn, navigate],
  );

  //⚠️ 一定要 useCallback，而且依賴只能放 gate?.to 不能放整個 gate。
  //
  //  這個 Provider 包著整個 app，任何一次 re-render 都會重新建立這個函式。
  //  它是以 onSuccess 傳給 LoginSheet 的，而那支的「渲染 Google 按鈕」effect
  //  依賴 onSuccess —— 參照一變就重跑，於是：
  //    1. google.accounts.id.initialize() 被重複呼叫（GSI 會在 console 警告）
  //    2. 更糟的是那個 effect 的 cleanup 會 el.innerHTML = ""，
  //       等於把已經畫好的按鈕砍掉重畫。使用者正要點的瞬間按鈕被換掉，
  //       就會出現「按了沒反應」這種抓不到的狀況。
  //
  //  依賴放 gate?.to 而不是 gate：gate 是物件，setGate 每次都給新物件，
  //  放整個 gate 等於沒有 useCallback。
  const handleSuccess = useCallback(() => {
    setGate(null);
    //登入完成後才把人帶去原本想去的頁面
    if (gate?.to) navigate(gate.to);
  }, [gate?.to, navigate]);

  //context 的值要 useMemo。這個 Provider 包著整個 app，
  //  直接寫 value={{ requireLogin }} 的話每次 render 都是新物件，
  //  所有用 useLoginGate 的元件都會跟著重畫 —— 包括不相干的頁面。
  const value = useMemo(() => ({ requireLogin }), [requireLogin]);

  return (
    <LoginGateContext.Provider value={value}>
      {children}

      <LoginSheet
        open={!!gate}
        message={gate?.message}
        onClose={() => setGate(null)}
        onSuccess={handleSuccess}
      />
    </LoginGateContext.Provider>
  );
};

//在 Provider 外呼叫時給明確錯誤，而不是讓 requireLogin 變成 undefined 才炸在使用點
export const useLoginGate = () => {
  const ctx = useContext(LoginGateContext);
  if (!ctx) throw new Error("useLoginGate 必須在 LoginGateProvider 內使用");
  return ctx;
};
